/**
 * Concordium WalletConnect Connector
 * Mirrors the behaviour of the Bitcoin WalletConnect connector while
 * keeping Concordium-specific request handling.
 */

import {
  WcHelpersUtil,
  WalletConnectConnector,
} from "@reown/appkit-controllers";
import {
  AccountAddress,
  AccountTransactionType,
  CborAccountAddress,
  CborMemo,
  TokenAmount,
  TokenId,
  createTokenUpdatePayload,
  getTransactionKindString,
  jsonUnwrapStringify,
  toBuffer,
} from "@concordium/web-sdk";
import {
  CCD_NAMESPACE,
  CCD_CHAIN_ID,
  CCD_WC_METHODS,
} from "../ccdConstants.js";
import {
  sessionHasCcdNamespace,
  sessionIsGenericOnly,
  sessionSupportsCcdChain,
} from "../ccdSessionUtils.js";
import { ProviderEventEmitter } from "../utils/ProviderEventEmitter.js";

const DEFAULT_CHAIN_ID = `${CCD_NAMESPACE}:${CCD_CHAIN_ID}`;
const GENERIC_NAMESPACE = "generic";

function bytesToHex(bytes) {
  return `0x${Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("")}`;
}

export class ConcordiumWalletConnectConnector extends WalletConnectConnector {
  constructor({ provider, chains, getActiveChain }) {
    super({ provider, caipNetworks: chains, namespace: CCD_NAMESPACE });
    this.caipNetworks = chains || this.caipNetworks;
    this.getActiveChain = getActiveChain;
    this.eventEmitter = new ProviderEventEmitter();
    this.emit = this.eventEmitter.emit.bind(this.eventEmitter);
    this.on = this.eventEmitter.on.bind(this.eventEmitter);
    this.removeListener = this.eventEmitter.removeListener.bind(
      this.eventEmitter
    );
  }

  get chains() {
    return this.sessionChains
      .map((chainId) =>
        this.caipNetworks?.find((network) => network.caipNetworkId === chainId)
      )
      .filter(Boolean);
  }

  get sessionChains() {
    const namespaces = this.provider?.session?.namespaces;
    if (!namespaces) {
      return [];
    }
    return WcHelpersUtil.getChainsFromNamespaces(namespaces);
  }

  get sessionNamespace() {
    const namespaces = this.provider?.session?.namespaces;
    if (!namespaces) {
      return CCD_NAMESPACE;
    }
    if (namespaces[CCD_NAMESPACE]) {
      return CCD_NAMESPACE;
    }
    if (namespaces[GENERIC_NAMESPACE]) {
      return GENERIC_NAMESPACE;
    }
    const namespaceKeys = Object.keys(namespaces);
    return namespaceKeys[0] || CCD_NAMESPACE;
  }

  hasCcdNamespace() {
    return sessionHasCcdNamespace(this.provider);
  }

  isGenericOnlySession() {
    return sessionIsGenericOnly(this.provider);
  }

  supportsConcordiumChain(chainId = DEFAULT_CHAIN_ID) {
    return sessionSupportsCcdChain(this.provider, chainId);
  }

  async connect() {
    return Promise.reject(
      new Error(
        "Connection of WalletConnectProvider should be done via UniversalAdapter"
      )
    );
  }

  getSessionAccounts() {
    const namespace = this.sessionNamespace;
    return this.provider?.session?.namespaces?.[namespace]?.accounts || [];
  }

  getSupportedChains(namespace = this.sessionNamespace) {
    const sessionNamespace = this.provider?.session?.namespaces?.[namespace];
    if (!sessionNamespace) {
      return [];
    }
    const chains = sessionNamespace.chains || [];
    const accounts = sessionNamespace.accounts || [];
    const accountChains = accounts
      .map((account) => {
        const parts = account.split(":");
        if (parts.length >= 2) {
          return `${parts[0]}:${parts[1]}`;
        }
        return null;
      })
      .filter(Boolean);
    return Array.from(new Set([...chains, ...accountChains]));
  }

  isChainSupported(chainId, namespace = this.sessionNamespace) {
    if (!chainId) {
      return false;
    }
    const candidate =
      chainId.includes(":") || namespace === GENERIC_NAMESPACE
        ? chainId
        : `${namespace}:${chainId}`;
    return this.getSupportedChains(namespace).includes(candidate);
  }

  extractAddress(account) {
    if (!account) {
      return "";
    }
    if (account.includes(":")) {
      const [, , address] = account.split(":");
      return address || account;
    }
    return account;
  }

  ensureProviderReady() {
    if (!this.provider) {
      throw new Error("Provider is not available");
    }
    if (!this.provider.session?.topic) {
      throw new Error(
        "Provider session is not available. Please connect your wallet first."
      );
    }
  }

  async internalRequest({ method, params }) {
    this.ensureProviderReady();
    const payload = { method, params };
    const chain = this.getActiveChain?.();
    const primaryChainId = chain?.caipNetworkId || DEFAULT_CHAIN_ID;
    const requestChainId = this.normalizeChainId(primaryChainId);

    try {
      return await this.performProviderRequest(payload, requestChainId);
    } catch (error) {
      if (this.shouldRetryWithoutChain(error)) {
        return await this.performProviderRequest(payload);
      }
      if (this.shouldRetryWithFallbackNamespace(error) && requestChainId) {
        const fallbackChainId = this.buildFallbackChainId(requestChainId);
        if (fallbackChainId) {
          return await this.performProviderRequest(payload, fallbackChainId);
        }
      }
      throw error;
    }
  }

  request(args) {
    return this.internalRequest(args);
  }

  async getAccountAddresses() {
    if (!this.provider) {
      return [];
    }

    const namespaceAccounts = this.getSessionAccounts();
    if (namespaceAccounts.length) {
      return namespaceAccounts.map((account) => ({
        address: this.extractAddress(account),
        type: "default",
      }));
    }

    this.checkIfMethodIsSupported(CCD_WC_METHODS.REQUEST_ACCOUNTS);

    const response = await this.internalRequest({
      method: CCD_WC_METHODS.REQUEST_ACCOUNTS,
      params: [],
    });

    const accounts = Array.isArray(response)
      ? response
      : response
      ? [response]
      : [];

    return accounts
      .map((account) => ({
        address: this.extractAddress(account),
        type: "default",
      }))
      .filter((entry) => entry.address);
  }

  async signMessage({ message, address }) {
    this.checkIfMethodIsSupported(CCD_WC_METHODS.SIGN_MESSAGE);
    return await this.internalRequest({
      method: CCD_WC_METHODS.SIGN_MESSAGE,
      params: { message, account: address, address },
    });
  }

  accountTransactionPayloadToJson(data) {
    return jsonUnwrapStringify(data, 0, (_key, value) => {
      if (value instanceof Uint8Array) {
        return bytesToHex(value);
      }
      if (value?.type === "Buffer") {
        return toBuffer(value.data).toString("hex");
      }
      return value;
    });
  }

  async sendTransaction({ transaction }) {
    if (!transaction?.amount || !transaction?.toAddress) {
      throw new Error("Transaction parameters are required");
    }

    this.checkIfMethodIsSupported(CCD_WC_METHODS.SEND_TRANSACTION);

    const accounts = this.getSessionAccounts();
    if (!accounts.length) {
      throw new Error(
        "No accounts found in session. Please connect your wallet first."
      );
    }

    const senderAccount = this.extractAddress(accounts[0]);
    const serializedPayload = this.accountTransactionPayloadToJson(transaction);

    const params = {
      type: getTransactionKindString(AccountTransactionType.Transfer),
      sender: senderAccount,
      payload: serializedPayload,
      schema: null,
    };

    try {
      const result = await this.internalRequest({
        method: CCD_WC_METHODS.SEND_TRANSACTION,
        params,
      });

      if (
        result &&
        typeof result === "object" &&
        "code" in result &&
        result.code === 500
      ) {
        throw new Error("transaction rejected in wallet");
      }

      return result?.hash || result?.txid || result;
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        "code" in error &&
        error.code === 500
      ) {
        throw new Error("transaction rejected in wallet");
      }
      console.error(
        "ConcordiumWalletConnectConnector: sendTransaction failed",
        error
      );
      throw error;
    }
  }

  async sendPltTransaction({ transaction }) {
    const {
      amount,
      toAddress,
      tokenId: tokenIdString,
      decimals: decimalsInput,
      memo,
    } = transaction || {};

    if (!amount || !toAddress || !tokenIdString) {
      throw new Error("Transaction parameters are required");
    }

    this.checkIfMethodIsSupported(CCD_WC_METHODS.SEND_TRANSACTION);

    const accounts = this.getSessionAccounts();
    if (!accounts.length) {
      throw new Error(
        "No accounts found in session. Please connect your wallet first."
      );
    }

    const tokenId = TokenId.fromString(tokenIdString);
    const senderAccount = this.extractAddress(accounts[0]);
    const decimals = decimalsInput !== undefined ? Number(decimalsInput) : 6;
    if (!Number.isFinite(decimals) || decimals < 0) {
      throw new Error("Invalid token decimals for PLT transaction");
    }

    const recipientAddress = AccountAddress.fromBase58(toAddress);
    const recipient = CborAccountAddress.fromAccountAddress(recipientAddress);
    const tokenAmount = TokenAmount.fromDecimal(
      amount.toString(),
      Number(decimals)
    );

    const transferOperation = {
      transfer: {
        amount: tokenAmount,
        recipient,
        ...(memo ? { memo: CborMemo.fromString(memo) } : {}),
      },
    };

    const payload = createTokenUpdatePayload(tokenId, transferOperation);
    const serializedPayload = this.accountTransactionPayloadToJson(payload);

    const params = {
      type: getTransactionKindString(AccountTransactionType.TokenUpdate),
      sender: senderAccount,
      payload: serializedPayload,
      schema: null,
    };

    try {
      const result = await this.internalRequest({
        method: CCD_WC_METHODS.SEND_TRANSACTION,
        params,
      });

      if (
        result &&
        typeof result === "object" &&
        "code" in result &&
        result.code === 500
      ) {
        throw new Error("transaction rejected in wallet");
      }

      return result?.hash || result?.txid || result;
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        "code" in error &&
        error.code === 500
      ) {
        throw new Error("transaction rejected in wallet");
      }
      console.error(
        "ConcordiumWalletConnectConnector: sendPltTransaction failed",
        error
      );
      throw error;
    }
  }

  async disconnect() {
    if (typeof this.provider?.disconnect === "function") {
      await this.provider.disconnect();
    }
  }

  checkIfMethodIsSupported(method) {
    const namespace = this.sessionNamespace;
    const supportedMethods =
      this.provider?.session?.namespaces?.[namespace]?.methods || [];
    if (supportedMethods.length > 0 && !supportedMethods.includes(method)) {
      throw new Error(`Method ${method} is not supported`);
    }
  }

  normalizeChainId(chainId) {
    if (!chainId) {
      return undefined;
    }
    const namespace = this.sessionNamespace;
    let candidate = chainId;
    if (namespace === GENERIC_NAMESPACE) {
      const [, reference] = chainId.split(":");
      candidate = reference
        ? `${GENERIC_NAMESPACE}:${reference}`
        : `${GENERIC_NAMESPACE}:${chainId}`;
    }
    if (!this.isChainSupported(candidate, namespace)) {
      return undefined;
    }
    return candidate;
  }

  buildFallbackChainId(chainId) {
    const namespace = this.sessionNamespace;
    if (!chainId || namespace === CCD_NAMESPACE) {
      return undefined;
    }
    if (namespace === GENERIC_NAMESPACE) {
      const [, reference] = chainId.split(":");
      const fallback = reference
        ? `${GENERIC_NAMESPACE}:${reference}`
        : undefined;
      return this.isChainSupported(fallback, namespace) ? fallback : undefined;
    }
    return undefined;
  }

  shouldRetryWithoutChain(error) {
    if (!error?.message) {
      return false;
    }
    return /connect\(\) before request/i.test(error.message);
  }

  shouldRetryWithFallbackNamespace(error) {
    if (!error?.message) {
      return false;
    }
    return /Namespace 'generic' is not configured/i.test(error.message);
  }

  async performProviderRequest(payload, chainId) {
    if (
      this.provider.client &&
      typeof this.provider.client.request === "function"
    ) {
      return await this.provider.client.request({
        topic: this.provider.session.topic,
        chainId,
        request: payload,
      });
    }

    if (typeof this.provider.request === "function") {
      if (chainId) {
        return await this.provider.request(
          {
            topic: this.provider.session.topic,
            request: payload,
          },
          chainId
        );
      }
      return await this.provider.request({
        topic: this.provider.session.topic,
        request: payload,
      });
    }

    throw new Error("Provider request method not available");
  }
}
